import { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  Save,
  AlertTriangle,
  LayoutGrid,
  CheckCircle2,
  Users,
  Monitor,
  Check,
  X,
  RefreshCw,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { AppBreadcrumbs } from '@/components/layout/AppBreadcrumbs';
import { PageContainer } from '@/components/layout/PageContainer';
import { HeroQuickMenuCard } from '@/components/shared/HeroQuickMenuCard';
import { eventService } from '@/lib/api/event.service';
import { guestService } from '@/lib/api/guest.service';
import { venueRoomService } from '@/lib/api/venue-room.service';
import { celebrationService } from '@/lib/api/celebration.service';
import { seatingApi } from '@/lib/api/seating.api';
import { SeatingBoard } from '@/components/seating/SeatingBoard';
import type { Event } from '@/types/event.types';
import type { VenueRoomWithConfigs, RoomSeatingConfig } from '@/types/venue-room.types';
import type { Celebration } from '@/types/celebration.types';
import type { Guest } from '@/types/guest.types';
import {
  SEATING_CONFIG_LABELS,
  SEATING_CONFIG_DESCRIPTIONS,
  SEATING_CONFIG_PARAMS,
  ROOM_TYPE_LABELS,
  calculateSeatingCapacity,
  suggestSeatingParams,
  SeatingConfigType,
} from '@/types/venue-room.types';
import React from 'react';

/** Room card for display and selection */
const RoomCard: React.FC<{
  room: VenueRoomWithConfigs;
  isAssigned?: boolean;
  showSelectButton?: boolean;
  onSelect?: (roomId: number, config?: string) => void;
  onRemove?: () => void;
  onChangeSeatingConfig?: (config: string) => void;
  disabled?: boolean;
  seatingConfig?: SeatingConfigType;
}> = ({ room, isAssigned, showSelectButton, onSelect, onRemove, onChangeSeatingConfig, disabled, seatingConfig }) => {
  const [selectedSeatingConfig, setSelectedSeatingConfig] = React.useState<string | undefined>(
    room.seating_configs?.find(sc => sc.is_default)?.config_type
  );
  const { t } = useTranslation(['seating']);

  return (
    <div className="rounded-lg border p-3 transition-colors hover:bg-muted/30">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-semibold text-sm">{room.name}</h3>
            <Badge variant="outline" className="text-[10px] px-1.5 py-0">
              {ROOM_TYPE_LABELS[room.room_type] || room.room_type}
            </Badge>
          </div>

          {room.description && (
            <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{room.description}</p>
          )}

          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5 text-xs text-muted-foreground">
            {room.max_capacity && (
              <span className="flex items-center gap-1">
                <Users className="h-3 w-3" />
                {t('seating:setup.roomCard.upTo', { count: room.max_capacity })}
              </span>
            )}
            {room.floor_area_sqft && (
              <span>{room.floor_area_sqft.toLocaleString()} sq ft</span>
            )}
            {room.has_av_equipment && (
              <span className="flex items-center gap-1">
                <Monitor className="h-3 w-3" />
                AV
              </span>
            )}
            {room.has_stage && <span>{t('seating:setup.roomCard.stage')}</span>}
            {room.has_dance_floor && <span>{t('seating:setup.roomCard.danceFloor')}</span>}
            {room.is_accessible && <span>{t('seating:setup.roomCard.accessible')}</span>}
          </div>

          {/* Active seating config badge for assigned room */}
          {isAssigned && seatingConfig && (
            <div className="mt-1.5">
              <Badge variant="secondary" size="sm" className="text-[10px]">
                {SEATING_CONFIG_LABELS[seatingConfig as keyof typeof SEATING_CONFIG_LABELS] || seatingConfig}
              </Badge>
            </div>
          )}

          {/* Seating config selector for picker mode */}
          {!isAssigned && room.seating_configs.length > 0 && showSelectButton && (
            <div className="mt-2.5">
              <label className="text-xs font-medium text-muted-foreground block mb-1.5">
                {t('seating:setup.roomCard.seatingLayout')} {!selectedSeatingConfig && <span className="text-destructive">*</span>}
              </label>
              <div className="flex flex-wrap gap-1.5">
                {room.seating_configs.map(sc => (
                  <Badge
                    key={sc.config_type}
                    variant={selectedSeatingConfig === sc.config_type ? 'default' : 'outline'}
                    className={`text-[11px] px-2 py-0.5 cursor-pointer transition-all ${
                      selectedSeatingConfig === sc.config_type
                        ? 'font-medium'
                        : 'hover:bg-primary hover:text-primary-foreground hover:border-primary'
                    }`}
                    onClick={() => setSelectedSeatingConfig(sc.config_type)}
                  >
                    {selectedSeatingConfig === sc.config_type && <Check className="h-3 w-3 mr-1" />}
                    {SEATING_CONFIG_LABELS[sc.config_type]}
                    {sc.capacity ? ` (${sc.capacity})` : ''}
                    {sc.is_default && !selectedSeatingConfig ? ' ★' : ''}
                  </Badge>
                ))}
              </div>
              {!selectedSeatingConfig && (
                <p className="text-[10px] text-muted-foreground mt-1">{t('seating:setup.roomCard.selectLayout')}</p>
              )}
            </div>
          )}

          {/* Warning for rooms without seating configs */}
          {!isAssigned && room.seating_configs.length === 0 && showSelectButton && (
            <div className="mt-2 flex items-start gap-1.5 px-2 py-1.5 bg-amber-50 border border-amber-200 rounded text-[11px] text-amber-700">
              <span>⚠️</span>
              <span>{t('seating:setup.roomCard.noLayouts')}</span>
            </div>
          )}

          {/* Other available configs (when assigned with change handler) */}
          {isAssigned && !showSelectButton && onChangeSeatingConfig && room.seating_configs.length > 0 && (
            <div className="mt-2">
              {room.seating_configs.filter(sc => sc.config_type !== seatingConfig).length > 0 && (
                <div>
                  <span className="text-[10px] text-muted-foreground uppercase tracking-wide font-medium block mb-1">
                    Other Available (click to switch)
                  </span>
                  <div className="flex flex-wrap gap-1">
                    {room.seating_configs
                      .filter(sc => sc.config_type !== seatingConfig)
                      .map(sc => (
                        <Badge
                          key={sc.config_type}
                          variant="outline"
                          className="text-[10px] px-1.5 py-0 font-normal text-muted-foreground cursor-pointer hover:bg-primary hover:text-primary-foreground hover:border-primary transition-colors"
                          onClick={!disabled ? () => onChangeSeatingConfig(sc.config_type) : undefined}
                        >
                          {SEATING_CONFIG_LABELS[sc.config_type]}
                          {sc.capacity ? ` (${sc.capacity})` : ''}
                        </Badge>
                      ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center gap-1 flex-shrink-0">
          {/* Remove button for assigned room */}
          {isAssigned && onRemove && (
            <button
              type="button"
              onClick={onRemove}
              disabled={disabled}
              className="p-1 rounded-md text-muted-foreground/50 hover:text-destructive hover:bg-destructive/10 transition-colors disabled:opacity-50"
              title="Remove room assignment"
            >
              <X className="h-4 w-4" />
            </button>
          )}

          {/* Select button for picker mode */}
          {showSelectButton && onSelect && (
            <Button
              variant="outline"
              size="xs"
              onClick={() => onSelect(room.id, selectedSeatingConfig)}
              disabled={disabled || !selectedSeatingConfig}
            >
              Select
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default function SeatingSetup() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation(['seating', 'common']);
  const eventId = Number(id);

  // ---- State ----
  const [event, setEvent] = useState<Event | null>(null);
  const [celebration, setCelebration] = useState<Celebration | null>(null);
  const [venueRoom, setVenueRoom] = useState<VenueRoomWithConfigs | null>(null);
  const [guests, setGuests] = useState<Guest[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [acceptedGuests, setAcceptedGuests] = useState(0);
  const [totalGuests, setTotalGuests] = useState(0);
  const [showVisualization, setShowVisualization] = useState(false);
  const [generatingLayout, setGeneratingLayout] = useState(false);
  const [venueRooms, setVenueRooms] = useState<VenueRoomWithConfigs[]>([]);
  const [roomSelectorOpen, setRoomSelectorOpen] = useState(false);
  const [roomSaving, setRoomSaving] = useState(false);

  // Parameter values — the core state
  const [params, setParams] = useState<Record<string, number>>({});
  // Track if user has made changes
  const [isDirty, setIsDirty] = useState(false);

  // ---- Derived ----
  const configType = event?.seating_config as SeatingConfigType | undefined;
  const paramDefs = configType ? SEATING_CONFIG_PARAMS[configType] : [];
  const configLabel = configType ? SEATING_CONFIG_LABELS[configType] : '';
  const configDescription = configType ? SEATING_CONFIG_DESCRIPTIONS[configType] : '';

  // Find the matching room seating config to get default_params
  const roomConfig: RoomSeatingConfig | undefined = useMemo(() => {
    if (!venueRoom || !configType) return undefined;
    return venueRoom.seating_configs.find(sc => sc.config_type === configType);
  }, [venueRoom, configType]);

  // Calculate capacity
  const calculatedCapacity = useMemo(() => {
    if (!configType || Object.keys(params).length === 0) return null;
    return calculateSeatingCapacity(configType, params);
  }, [configType, params]);

  // Check capacity warnings
  const capacityWarning = useMemo(() => {
    if (calculatedCapacity === null || calculatedCapacity === 0) return null;
    if (acceptedGuests > calculatedCapacity) {
      return `Accepted guests (${acceptedGuests}) exceed seating capacity (${calculatedCapacity})`;
    }
    if (totalGuests > calculatedCapacity) {
      return `Total guests (${totalGuests}) exceed seating capacity (${calculatedCapacity}). Some may not have seats.`;
    }
    return null;
  }, [calculatedCapacity, acceptedGuests, totalGuests]);

  // ---- Load data ----
  useEffect(() => {
    async function load() {
      if (!eventId || isNaN(eventId)) return;
      setLoading(true);
      try {
        const [eventData, guestsData] = await Promise.all([
          eventService.getEvent(eventId),
          guestService.getEventGuests(eventId),
        ]);
        setEvent(eventData);
        setGuests(guestsData);
        setTotalGuests(guestsData.length);
        setAcceptedGuests(guestsData.filter(g => g.rsvp_status === 'accepted').length);

        // Load celebration
        if (eventData.celebration_id) {
          const celebData = await celebrationService.getCelebration(eventData.celebration_id);
          setCelebration(celebData);
        }

        // Load venue rooms (all rooms) + assigned room
        if (eventData.venue_id) {
          const allRooms = await venueRoomService.getRooms(eventData.venue_id);
          setVenueRooms(allRooms);
          if (eventData.venue_room_id) {
            const assigned = allRooms.find((r: VenueRoomWithConfigs) => r.id === eventData.venue_room_id);
            if (assigned) setVenueRoom(assigned);
          }
        }

        // Initialize params from event.seating_params, then guest-count suggestion, then room defaults
        const savedParams = eventData.seating_params;
        if (savedParams && Object.keys(savedParams).length > 0) {
          setParams(savedParams);
        } else if (eventData.seating_config && guestsData.length > 0) {
          // Auto-prefill from total guest count (all guests, regardless of RSVP)
          const suggested = suggestSeatingParams(
            eventData.seating_config as SeatingConfigType,
            guestsData.length
          );
          if (Object.keys(suggested).length > 0) {
            setParams(suggested);
          }
        } else if (eventData.venue_id && eventData.venue_room_id && eventData.seating_config) {
          // Fall back to room config defaults
          try {
            const roomData = await venueRoomService.getRoom(eventData.venue_id, eventData.venue_room_id);
            const matchingConfig = roomData.seating_configs.find(
              sc => sc.config_type === eventData.seating_config
            );
            if (matchingConfig?.default_params) {
              setParams(matchingConfig.default_params);
            }
          } catch {
            // Ignore — defaults not available
          }
        }

        // Store event locally for checking seating_generated flag
        if (eventData.seating_generated) {
          setShowVisualization(true);
        }
      } catch (err) {
        setError(t('seating:setup.errors.loadFailed'));
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [eventId]);

  // ---- Handlers ----
  const handleParamChange = useCallback((key: string, value: string) => {
    const numValue = value === '' ? 0 : parseInt(value, 10);
    if (isNaN(numValue)) return;
    setParams(prev => ({ ...prev, [key]: numValue }));
    setIsDirty(true);
    setSaved(false);
  }, []);

  const handleSave = async () => {
    if (!event) return;
    setSaving(true);
    setError(null);
    try {
      await eventService.updateEvent(event.id, { seating_params: params });
      setEvent(prev => prev ? { ...prev, seating_params: params, seating_generated: false } : prev);
      setIsDirty(false);
      setSaved(true);
      // Reset visualization since params changed — layout needs to be regenerated
      setShowVisualization(false);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(t('seating:setup.errors.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleLoadDefaults = useCallback(() => {
    if (roomConfig?.default_params) {
      setParams(roomConfig.default_params);
      setIsDirty(true);
      setSaved(false);
    }
  }, [roomConfig]);

  const handleSuggestFromGuests = useCallback(() => {
    if (!configType) return;
    const suggested = suggestSeatingParams(configType, totalGuests);
    setParams(suggested);
    setIsDirty(true);
    setSaved(false);
  }, [configType, totalGuests]);

  // Generate seating layout
  const handleGenerateLayout = async () => {
    if (!event || !configType || !roomConfig) {
      setError(t('seating:setup.errors.missingConfig'));
      return;
    }

    setGeneratingLayout(true);
    setError(null);
    
    try {
      const response = await seatingApi.generateLayout(event.id, {
        seatingConfigId: roomConfig.id,
        configType,
        params
      });

      if (response.success) {
        setShowVisualization(true);
        // Update event to mark layout as generated
        setEvent(prev => prev ? { ...prev, seating_generated: true } : prev);
      } else {
        setError(response.error || t('seating:setup.errors.generateFailed'));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('seating:setup.errors.generateFailed'));
    } finally {
      setGeneratingLayout(false);
    }
  };

  // ---- Room selection handler ----
  const handleRoomSelect = async (roomId: number, config?: string) => {
    if (!event) return;
    setRoomSaving(true);
    setError(null);
    try {
      await eventService.updateEvent(event.id, {
        venue_room_id: roomId,
        seating_config: config || undefined,
      });
      // Refresh local state
      const updatedEvent = await eventService.getEvent(event.id);
      setEvent(updatedEvent);
      if (updatedEvent.venue_id && updatedEvent.venue_room_id) {
        const roomData = await venueRoomService.getRoom(updatedEvent.venue_id, updatedEvent.venue_room_id);
        setVenueRoom(roomData);
      }
      // Reset visualization since room/config changed
      setShowVisualization(updatedEvent.seating_generated ?? false);
      setRoomSelectorOpen(false);
      // Reset params to defaults for new config
      if (config && updatedEvent.venue_id && updatedEvent.venue_room_id) {
        const roomData = await venueRoomService.getRoom(updatedEvent.venue_id, updatedEvent.venue_room_id);
        const matchingConfig = roomData.seating_configs.find(sc => sc.config_type === config);
        if (matchingConfig?.default_params) {
          setParams(matchingConfig.default_params);
          setIsDirty(true);
        }
      }
    } catch {
      setError(t('seating:setup.errors.roomUpdateFailed'));
    } finally {
      setRoomSaving(false);
    }
  };

  const handleChangeSeatingConfig = async (newConfig: string) => {
    if (!event || !venueRoom) return;
    await handleRoomSelect(venueRoom.id, newConfig);
  };

  const handleRemoveRoom = async () => {
    if (!event) return;
    setRoomSaving(true);
    setError(null);
    try {
      await eventService.updateEvent(event.id, {
        venue_room_id: undefined,
        seating_config: undefined,
      });
      const updatedEvent = await eventService.getEvent(event.id);
      setEvent(updatedEvent);
      setVenueRoom(null);
      setShowVisualization(false);
      setParams({});
      setIsDirty(false);
    } catch {
      setError(t('seating:setup.errors.roomRemoveFailed'));
    } finally {
      setRoomSaving(false);
    }
  };

  // ---- Render ----
  if (loading) {
    return (
      <PageContainer width="wide" className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/3" />
          <div className="h-64 bg-gray-200 rounded" />
        </div>
      </PageContainer>
    );
  }

  if (!event) {
    return (
      <PageContainer width="wide" className="p-6 text-center">
        <p className="text-muted-foreground">{t('seating:setup.notFound')}</p>
        <Button variant="outline" onClick={() => navigate(-1)} className="mt-4">
          <ArrowLeft className="h-4 w-4 mr-2" /> {t('seating:setup.goBack')}
        </Button>
      </PageContainer>
    );
  }

  const layoutStatusLabel = !event.venue_room_id || !configType
    ? t('seating:setup.values.notSet')
    : ((showVisualization || event.seating_generated) ? t('seating:setup.values.generated') : t('seating:setup.values.pending'));

  const renderHeroCard = (subtitle: string) => (
    <HeroQuickMenuCard
      borderClassName="border-primary/20"
      heroClassName="bg-gradient-to-br from-primary/10 via-primary/[0.04] to-white"
      heroContent={
        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 rounded-xl bg-primary/10 ring-1 ring-primary/20 flex items-center justify-center flex-shrink-0">
              <LayoutGrid className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h1 className="text-xl font-bold tracking-tight text-foreground">{t('seating:setup.title')}</h1>
              <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate(`/events/${event.id}`)}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t('seating:setup.backToEvent')}
            </Button>
          </div>
        </div>
      }
      columns={4}
      items={[
        {
          key: 'room',
          icon: Monitor,
          iconClassName: 'text-blue-600',
          hoverClassName: 'hover:bg-blue-50',
          label: t('seating:setup.stats.room'),
          value: venueRoom?.name ?? t('seating:setup.values.notSelected'),
        },
        {
          key: 'config',
          icon: LayoutGrid,
          iconClassName: 'text-violet-600',
          hoverClassName: 'hover:bg-violet-50',
          label: t('seating:setup.stats.layout'),
          value: configLabel || t('seating:setup.values.notSelected'),
        },
        {
          key: 'guests',
          icon: Users,
          iconClassName: 'text-amber-600',
          hoverClassName: 'hover:bg-amber-50',
          label: t('seating:setup.stats.guests'),
          value: totalGuests,
        },
        {
          key: 'layout-status',
          icon: CheckCircle2,
          iconClassName: 'text-emerald-600',
          hoverClassName: 'hover:bg-emerald-50',
          label: t('seating:setup.stats.status'),
          value: layoutStatusLabel,
        },
      ]}
    />
  );

  // No room or config selected — show inline room picker
  if (!event.venue_room_id || !configType) {
    return (
      <PageContainer width="wide" className="space-y-4">
        <AppBreadcrumbs
          items={
            celebration
              ? [
                  { label: 'Home', to: '/home' },
                  { label: 'Celebrations', to: '/celebrations' },
                  { label: celebration.title, to: `/celebrations/${celebration.id}` },
                  { label: event.title, to: `/events/${event.id}` },
                  { label: 'Seating Setup' },
                ]
              : [
                  { label: 'Home', to: '/home' },
                  { label: 'Events', to: '/events' },
                  { label: event.title, to: `/events/${event.id}` },
                  { label: 'Seating Setup' },
                ]
          }
        />

        {renderHeroCard(t('seating:setup.heroSubtitles.selectRoom', { eventTitle: event.title }))}

        {/* Room Picker */}
        <Card>
          <CardContent className="p-4">
            <h2 className="text-sm font-semibold mb-3">{t('seating:setup.roomPicker.title')}</h2>
            {venueRooms.length === 0 ? (
              <div className="text-center py-6">
                <LayoutGrid className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                <p className="text-sm text-muted-foreground mb-3">{t('seating:setup.roomPicker.noRooms')}</p>
                <Button variant="outline" size="sm" onClick={() => navigate(`/events/${event.id}`)}>
                  <ArrowLeft className="h-4 w-4 mr-2" /> {t('seating:setup.backToEvent')}
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                {venueRooms.map(room => (
                  <RoomCard
                    key={room.id}
                    room={room}
                    showSelectButton
                    onSelect={handleRoomSelect}
                    disabled={roomSaving}
                  />
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Error */}
        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
            {error}
          </div>
        )}
      </PageContainer>
    );
  }

  return (
    <PageContainer width="wide" className="space-y-4">
      <AppBreadcrumbs
        items={
          celebration
            ? [
                { label: 'Home', to: '/home' },
                { label: 'Celebrations', to: '/celebrations' },
                { label: celebration.title, to: `/celebrations/${celebration.id}` },
                { label: event.title, to: `/events/${event.id}` },
                { label: 'Seating Setup' },
              ]
            : [
                { label: 'Home', to: '/home' },
                { label: 'Events', to: '/events' },
                { label: event.title, to: `/events/${event.id}` },
                { label: 'Seating Setup' },
              ]
        }
      />

        {renderHeroCard(t('seating:setup.heroSubtitles.configureLayout', { eventTitle: event.title }))}

      {/* Selected Layout Info */}
      <Card>
        <CardContent className="p-4">
          {venueRoom && (
            <RoomCard
              room={venueRoom}
              isAssigned
              seatingConfig={configType}
              onRemove={handleRemoveRoom}
              onChangeSeatingConfig={handleChangeSeatingConfig}
              disabled={roomSaving}
            />
          )}
          {!venueRoom && (
            <div className="flex items-start gap-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <Badge variant="default">{configLabel}</Badge>
                </div>
                <p className="text-sm text-muted-foreground">{configDescription}</p>
              </div>
            </div>
          )}

          {/* Change Room button */}
          <div className="mt-3 pt-3 border-t flex items-center justify-between">
            <div className="flex items-center gap-2">
              {roomConfig?.capacity && (
                <span className="text-xs text-muted-foreground">
                  {t('seating:setup.selected.configCapacity')} <strong>{roomConfig.capacity}</strong>
                </span>
              )}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setRoomSelectorOpen(!roomSelectorOpen)}
            >
              <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
              {roomSelectorOpen ? t('seating:setup.selected.cancel') : t('seating:setup.selected.changeRoom')}
            </Button>
          </div>

          {/* Inline room picker (toggle) */}
          {roomSelectorOpen && (
            <div className="mt-3 pt-3 border-t space-y-2">
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{t('seating:setup.selected.availableRooms')}</h3>
              {venueRooms.filter(r => r.id !== venueRoom?.id).length === 0 ? (
                <p className="text-sm text-muted-foreground py-2">{t('seating:setup.selected.noOtherRooms')}</p>
              ) : (
                venueRooms
                  .filter(r => r.id !== venueRoom?.id)
                  .map(room => (
                    <RoomCard
                      key={room.id}
                      room={room}
                      showSelectButton
                      onSelect={handleRoomSelect}
                      disabled={roomSaving}
                    />
                  ))
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Layout Parameters & Capacity */}
      <Card>
        <CardContent className="p-4 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold">{t('seating:setup.params.title')}</h2>
            <div className="flex items-center gap-2">
              {totalGuests > 0 && configType && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleSuggestFromGuests}
                  title={t('seating:setup.params.recalculate', { count: totalGuests })}
                >
                  <Users className="h-3.5 w-3.5 mr-1.5" />
                  {t('seating:setup.params.recalculate', { count: totalGuests })}
                </Button>
              )}
              {roomConfig?.default_params && Object.keys(roomConfig.default_params).length > 0 && (
                <Button variant="outline" size="sm" onClick={handleLoadDefaults}>
                  {t('seating:setup.params.loadDefaults')}
                </Button>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {paramDefs.map(def => (
              <div key={def.key}>
                <label className="block text-sm font-medium text-foreground mb-1">
                  {def.label}
                </label>
                <input
                  type="number"
                  min={def.min}
                  max={def.max}
                  placeholder={def.placeholder}
                  value={params[def.key] || ''}
                  onChange={e => handleParamChange(def.key, e.target.value)}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                />
                {roomConfig?.default_params?.[def.key] !== undefined && (
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {t('seating:setup.params.roomDefault', { value: roomConfig.default_params[def.key] })}
                  </p>
                )}

              </div>
            ))}

            {paramDefs.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-4 col-span-full">
                {t('seating:setup.params.noParams')}
              </p>
            )}
          </div>

          {/* Capacity row — inline with params */}
          {paramDefs.length > 0 && (
            <div className="flex items-center gap-4 pt-3 border-t">
              <div className="flex items-center gap-6 flex-1">
                <div className="text-center">
                  <div className="text-xl font-bold text-primary">
                    {calculatedCapacity !== null && calculatedCapacity > 0 ? calculatedCapacity : '—'}
                  </div>
                  <div className="text-xs text-muted-foreground">{t('seating:setup.params.seats')}</div>
                </div>
                <div className="text-center">
                  <div className="text-xl font-bold text-blue-600">{acceptedGuests}</div>
                  <div className="text-xs text-muted-foreground">{t('seating:setup.params.accepted')}</div>
                </div>
                <div className="text-center">
                  <div className="text-xl font-bold text-gray-600">{totalGuests}</div>
                  <div className="text-xs text-muted-foreground">{t('seating:setup.params.total')}</div>
                </div>
              </div>

              {/* Capacity Warning */}
              {capacityWarning && (
                <div className="p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-700 flex items-start gap-2 flex-1">
                  <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                  <span>{capacityWarning}</span>
                </div>
              )}
            </div>
          )}

          {/* Generate Layout Button */}
          {!showVisualization && paramDefs.length > 0 && (
            <div className="pt-3 border-t">
              <Button 
                onClick={handleGenerateLayout}
                disabled={generatingLayout || paramDefs.length === 0}
                className="w-full"
              >
                {generatingLayout ? t('seating:setup.params.generatingButton') : t('seating:setup.params.generateButton')}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Seating Visualization (v2 — layered HTML/CSS) */}
      {showVisualization && (
        <SeatingBoard
          eventId={event.id}
          guests={guests}
          readOnly={true}
          assignmentUrl={`/events/${event.id}/guests#seating`}
          onAssignmentChange={() => {
            // Assignment happens from the Guests page
          }}
        />
      )}

      {/* Error */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
          {error}
        </div>
      )}

      {/* Save Bar */}
      <div className="flex items-center justify-between sticky bottom-4 bg-white border rounded-lg p-3 shadow-lg">
        <div className="flex items-center gap-2">
          {isDirty && (
            <span className="text-sm text-amber-600 flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-amber-500" />
              {t('seating:setup.saveBar.unsaved')}
            </span>
          )}
          {saved && (
            <span className="text-sm text-green-600 flex items-center gap-1.5">
              <CheckCircle2 className="h-4 w-4" />
              {t('seating:setup.saveBar.saved')}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(`/events/${event.id}`)}
          >
            {t('seating:setup.saveBar.cancel')}
          </Button>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={saving || !isDirty || paramDefs.length === 0}
          >
            {saving ? (
              <>{t('seating:setup.saveBar.saving')}</>
            ) : (
              <>
                <Save className="h-4 w-4 mr-1" />
                {t('seating:setup.saveBar.save')}
              </>
            )}
          </Button>
        </div>
      </div>
    </PageContainer>
  );
}
